/* External */
import { connect } from 'react-redux';

/* Ott-common */
import { isWithinIntervalDate } from '@dtvgo/date-utils';

/* Components */
import SportSection from '../SportSection';
import FeaturedCarouselCard from '../FeaturedCarouselCard';
import CardStationWithSchedule from '../CardStationWithSchedule';
import CollectionCard from '../CollectionCard';
import CardContinueWatching from '../CardContinueWatching';
import ImageBanner from '../ImageBanner';
import LiveCard from '../LiveCard';
import CardVod from '../CardVod';
import SeeAllCardVod from '../SeeAllCardVod';
import SeeAllCardLive from '../SeeAllCardLive';
import GroupStanding from '../GroupStanding';
import PlayOffs from '../PlayOffs';

/* Utils */
import {
  createRedirectFromCollection,
  redirectTo,
  redirectToDetail,
  redirectToPlayer,
} from '../../utils/history';
import { SOURCE_CAROUSEL_ID, PageType } from '../../utils/constants';
import { getPlaySourcePage, PLAY_SOURCE_SECTION } from '../../utils/trackSourceConviva';
import { isShow, isSchedule } from '../../utils/metaDataUtils';
import { getLink, Routes } from '../../utils/router';
import { CardType } from '../../utils/cardsUtils';
import { selectPolymorphicEntityById } from '../../storage/selectors';
import { COMPONENT_TYPES, GTM_EVENT_NAMES } from '../../utils/googleTags';

/* Storage */
import store from '../../storage/configureStore';
import { selectContentById } from '../../storage/contents';
import { selectShowById } from '../../storage/shows';

const withItem = connect((state, { item }) => ({ item: selectPolymorphicEntityById(item)(state) }));
const selectContents = ({ contents }) => contents;
const selectCollections = ({ collections }) => collections;

// Função utilitária para usar fora de componentes
export function getContentInfo(contentId) {
  if (!contentId) {
    return undefined;
  }

  const state = store.getState();

  const contentSelector = selectContentById(contentId);
  const content = contentSelector(state);

  if (content) {
    return content;
  }

  const showSelector = selectShowById(contentId);
  return showSelector(state);
}

/**
 * Handles click learn action for tracking purposes
 * @param {Object} params
 * @param {Object} params.contentInfo - Contain streaming and asset data
 * @param {string} [params.msoProvider='']
 * @param {string} [params.userType='']
 * @param {string} [params.playSource=''] - Play source identifier for tracking
 * @param {Function} params.fetchClickAction - Function to send click learn action data
 */
const handleClickLearnAction = ({
  contentInfo,
  msoProvider = '',
  userType = '',
  playSource = '',
  fetchClickAction,
}) => {
  if (!contentInfo) {
    console.warn('handleClickLearnAction: contentInfo is required');
    return;
  }

  if (!fetchClickAction || typeof fetchClickAction !== 'function') {
    console.warn('handleClickLearnAction: fetchClickAction must be a function');
    return;
  }

  const clickActionParams = {
    actionOrigin: contentInfo.actionOrigin || '',
    userOrigin: {
      playSource,
      userLogin: userType,
      msoProvider,
    },
    contentType: contentInfo.isLive ? 1 : 2,
    guideId: contentInfo?.streaming?.id || contentInfo?.vrioAssetId || '',
    vrioAssetId: contentInfo.vrioAssetId,
  };

  fetchClickAction(clickActionParams);
};

export const ComponentByCardType = {
  [CardType.FEATURED]: {
    CardComponent: withItem(FeaturedCarouselCard),
    itemsSelector: selectContents,
    handleEnterPressCreator: ({
      pageId,
      handleClickSection,
      locationState,
      carouselCommonTagEvent,
      msoProvider,
      userType,
      playSource,
      fetchClickAction,
    }) => ({ item }) => {
      const contentInfo = getContentInfo(
        isSchedule(item) ? item.content : undefined,
      ) || item;

      const state = {
        ...locationState({ content: item, componentType: CardType.FEATURED }),
        actionOrigin: contentInfo?.actionOrigin || '',
      };

      handleClickSection(true);
      carouselCommonTagEvent({
        eventName: GTM_EVENT_NAMES.CAROUSELS.WATCH_CAROUSELS,
        content: item,
        extraData: state,
      });

      handleClickLearnAction({
        contentInfo,
        msoProvider,
        userType,
        playSource,
        fetchClickAction,
      });
      if (isShow(item)) {
        redirectToDetail(item, { state });
      }
      redirectToPlayer(item, {
        page: getPlaySourcePage(pageId),
        section: PLAY_SOURCE_SECTION.FEATURED_CAROUSEL,
        state,
      });
    },
  },
  [CardType.CHANNEL]: {
    CardComponent: withItem(CardStationWithSchedule),
    itemsSelector: selectContents,
    handleEnterPressCreator: ({
      pageId,
      carousel,
      handleClickSection,
      locationState,
      carouselCommonTagEvent,
      msoProvider,
      userType,
      playSource,
      fetchClickAction,
    }) => (
      ({ item: station }) => {
        const contentInfo = getContentInfo(
          isSchedule(station) ? station.content : undefined,
        ) || station;

        const state = {
          ...locationState({
            content: station,
            componentType: COMPONENT_TYPES.RECENTLY_WATCHED,
            sectionName: COMPONENT_TYPES.RECENTLY_WATCHED,
          }),
          actionOrigin: contentInfo?.actionOrigin || '',
        };

        handleClickSection();
        carouselCommonTagEvent({
          eventName: GTM_EVENT_NAMES.CAROUSELS.WATCH_CAROUSELS,
          content: station,
          extraData: state,
        });

        handleClickLearnAction({
          contentInfo,
          msoProvider,
          userType,
          playSource,
          fetchClickAction,
        });
        redirectToPlayer(station, {
          page: getPlaySourcePage(pageId),
          section: carousel?.sourceCarouselId === SOURCE_CAROUSEL_ID.RECENTLY_WATCHED
            ? PLAY_SOURCE_SECTION.RECENTLY_WATCHED_CAROUSEL
            : carousel?.title,
          state,
        });
      }
    ),
  },
  [CardType.SPORT_SECTION]: {
    CardComponent: SportSection,
    itemsSelector: selectCollections,
    CarouselLengthByCardType: 32,
    handleEnterPressCreator: ({
      handleClickSection,
      carousel,
      pageId,
      carouselCommonTagEvent,
    }) => (
      createRedirectFromCollection({
        beforeRedirect: handleClickSection,
        carousel,
        pageId,
        carouselCommonTagEvent,
      })
    ),
  },
  [CardType.IMAGE_BANNER]: {
    CardComponent: ImageBanner,
    itemsSelector: selectCollections,
    CarouselLengthByCardType: 1,
    handleEnterPressCreator: ({
      handleClickSection,
      carousel,
      pageId,
      carouselCommonTagEvent,
    }) => (
      createRedirectFromCollection({
        beforeRedirect: handleClickSection,
        carousel,
        pageId,
        carouselCommonTagEvent,
      })
    ),
  },
  [CardType.COLLECTION]: {
    CardComponent: CollectionCard,
    itemsSelector: selectCollections,
    handleEnterPressCreator: ({
      handleClickSection,
      carousel,
      pageId,
      carouselCommonTagEvent,
    }) => (
      createRedirectFromCollection({
        beforeRedirect: handleClickSection,
        carousel,
        pageId,
        carouselCommonTagEvent,
      })
    ),
  },
  [CardType.CONTINUE_WATCHING]: {
    CardComponent: withItem(CardContinueWatching),
    itemsSelector: selectContents,
    handleEnterPressCreator: ({
      pageId,
      handleClickSection,
      locationState,
      carouselCommonTagEvent,
      msoProvider,
      userType,
      playSource,
      fetchClickAction,
    }) => (
      ({ item: content }) => {
        const contentInfo = getContentInfo(
          isSchedule(content) ? content.content : undefined,
        ) || content;

        const state = {
          ...locationState({
            content,
            componentType: COMPONENT_TYPES.CONTINUE_WATCHING,
            sectionName: COMPONENT_TYPES.CONTINUE_WATCHING,
          }),
          actionOrigin: contentInfo?.actionOrigin || '',
        };

        handleClickSection();
        carouselCommonTagEvent({
          eventName: GTM_EVENT_NAMES.CAROUSELS.WATCH_CAROUSELS,
          content,
          extraData: state,
        });

        handleClickLearnAction({
          contentInfo,
          msoProvider,
          userType,
          playSource,
          fetchClickAction,
        });
        redirectToPlayer(content, {
          page: getPlaySourcePage(pageId),
          section: PLAY_SOURCE_SECTION.RESUME_WATCHING_CAROUSEL,
          state,
        });
      }
    ),
  },
  [CardType.LIVE]: {
    CardComponent: withItem(LiveCard),
    itemsSelector: selectContents,
    handleEnterPressCreator: ({
      pageId,
      handleClickSection,
      carousel,
      locationState,
      carouselCommonTagEvent,
      msoProvider,
      userType,
      playSource,
      fetchClickAction,
    }) => (
      ({ item: schedule }) => {
        handleClickSection();
        const startDate = schedule?.startTime
          ? new Date(schedule?.startTime)
          : undefined;
        const endDate = schedule?.endTime ? new Date(schedule?.endTime) : undefined;
        const isLive = isWithinIntervalDate(startDate, endDate);

        const contentInfo = getContentInfo(
          isSchedule(schedule) ? schedule.content : undefined,
        ) || schedule;

        const state = {
          ...locationState({ content: schedule, componentType: CardType.LIVE }),
          actionOrigin: contentInfo?.actionOrigin || '',
        };

        carouselCommonTagEvent({
          eventName: GTM_EVENT_NAMES.CAROUSELS.WATCH_CAROUSELS,
          content: schedule,
          extraData: state,
          isLive: true,
        });

        handleClickLearnAction({
          contentInfo,
          msoProvider,
          userType,
          playSource,
          fetchClickAction,
        });

        if (isLive) {
          redirectToPlayer(schedule, {
            page: getPlaySourcePage(pageId),
            section:
              pageId === PageType.SPORTS
                ? PLAY_SOURCE_SECTION.LIVE_NOW_CAROUSEL
                : carousel?.title,
            state,
          });
        } else {
          redirectToDetail(schedule, { state });
        }
      }
    ),
    SeeAllCardComponent: SeeAllCardLive,
    handleSeeAllEnterPressCreator: ({
      carousel,
      handleClickSection,
      carouselCommonTagEvent,
      locationState,
    }) => () => {
      handleClickSection();
      carouselCommonTagEvent({
        eventName: GTM_EVENT_NAMES.CAROUSELS.SEE_MORE,
        extraData: {
          ...locationState({ componentType: CardType.LIVE }),
        },
        isLive: true,
      });
      const detailRoute = Routes.Private.Detail;
      redirectTo(getLink(detailRoute.Section, carousel));
    },
  },
  [CardType.VOD]: {
    CardComponent: withItem(CardVod),
    itemsSelector: selectContents,
    handleEnterPressCreator: ({
      handleClickSection,
      locationState,
      carouselCommonTagEvent,
      msoProvider,
      userType,
      playSource,
      fetchClickAction,
    }) => ({ content }) => {
      handleClickSection();

      const contentInfo = getContentInfo(content?.id || content?.vrioAssetId);

      const state = {
        ...locationState({ content, componentType: CardType.VOD }),
        actionOrigin: contentInfo?.actionOrigin || content?.actionOrigin || '',
      };

      carouselCommonTagEvent({
        eventName: GTM_EVENT_NAMES.CAROUSELS.WATCH_CAROUSELS,
        content,
        extraData: state,
      });

      handleClickLearnAction({
        contentInfo,
        msoProvider,
        userType,
        playSource,
        fetchClickAction,
      });
      redirectToDetail(content, { state });
    },
    SeeAllCardComponent: SeeAllCardVod,
    handleSeeAllEnterPressCreator: ({
      carousel,
      handleClickSection,
      carouselCommonTagEvent,
      locationState,
    }) => () => {
      handleClickSection();
      carouselCommonTagEvent({
        eventName: GTM_EVENT_NAMES.CAROUSELS.SEE_MORE,
        extraData: {
          ...locationState({ componentType: CardType.VOD }),
        },
      });
      if (carousel?.sourceCarouselId === SOURCE_CAROUSEL_ID.MY_LIST) {
        redirectTo(getLink(Routes.Private.MyList));
      } else {
        const detailRoute = Routes.Private.Detail;
        redirectTo(getLink(detailRoute.Section, carousel));
      }
    },
  },
  [CardType.PLAY_OFFS]: {
    CardComponent: PlayOffs,
    itemsSelector: (carousel) => [carousel?.sport?.standing],
    CarouselLengthByCardType: 1,
  },
  [CardType.GROUP_STANDING]: {
    CardComponent: GroupStanding,
    itemsSelector: (carousel) => carousel?.sport?.standing?.groups,
    CarouselLengthByCardType: 8,
  },
};
